import React, { useEffect, useState } from 'react';
import IdPasswordForm from './IdPasswordForm';
import NewPasswordForm from './NewPasswordForm';
import EmailCodeForm from './EmailCodeForm';
import { RootState, useDispatch, useSelector } from '../../../redux/store';
import { cognitoLogin, completeNewPassword, completeEmailCode } from '../../../redux/slices/session';
import type { SignInInput } from '@aws-amplify/auth';

const LoginForm = (): React.ReactElement => {
  const dispatch = useDispatch();
  const { isLoading, emailCodeRequired, newPasswordRequired } = useSelector((state: RootState) => state.session);

  // ログイン時の入力値（Eメール・パスワード）
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const isValidIdPassword: boolean = email.length > 0 && password.length > 0;

  // 新規パスワード設定時の入力値（新規パスワード・新規パスワード確認用）
  const [password1, setPassword1] = useState<string>('');
  const [password2, setPassword2] = useState<string>('');
  const isValidNewPassword: boolean = password1.length && password2.length && password1 === password2;

  // MFA時の入力値（Eメールコード）
  const [emailCode, setEmailCode] = useState<string>('');
  const isValidEmailCode: boolean = emailCode.length > 0;

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  // }, []);

  // 新規パスワード設定時にはメールアドレスとパスワードをクリアする
  useEffect(() => {
    if (newPasswordRequired) {
      setEmail(() => '');
      setPassword(() => '');
    }
  }, [newPasswordRequired]);

  // EVENT HANDLER
  // ================================================================================

  /**
   * ログイン時のEメール・パスワード入力のイベントハンドラ。
   * @param {React.ChangeEvent<HTMLInputElement>} event - inputのchangeイベント
   * @returns {void}
   */
  const handleIdPasswordChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const target: string = event.target.id;
    const value: string = event.target.value;
    if (target === 'email') {
      setEmail(value);
    } else if (target === 'password') {
      setPassword(value);
    } else {
      // do nothing
    }
  };

  /**
   * ログインボタン押下時のイベントハンドラ。
   * @param {React.SyntheticEvent<HTMLFormElement>} event - buttonのsubmitイベント
   * @returns {Promise<void>}
   */
  const handleIdPasswordSubmit = (event: React.SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const params: SignInInput = {
      username: email,
      password,
    };
    dispatch(cognitoLogin(params));
  };

  /**
   * 新規パスワード設定時の新規パスワード・新規パスワード確認用入力のイベントハンドラ。
   * @param {React.ChangeEvent<HTMLInputElement>} event - inputのchangeイベント
   * @returns {void}
   */
  const handleNewPasswordChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const target = event.target.id;
    const value = event.target.value;
    if (target === 'password1') {
      setPassword1(value);
    } else if (target === 'password2') {
      setPassword2(value);
    } else {
      // do nothing
    }
  };

  /**
   * 新規パスワード設定ボタン押下時のイベントハンドラ。
   * @param {React.SyntheticEvent<HTMLFormElement>} event - buttonのsubmitイベント
   * @returns {void}
   */
  const handleNewPasswordSubmit = (event: React.SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    dispatch(completeNewPassword(password1));
  };

  /**
   * NFA時のEメールコード入力のイベントハンドラ。
   * @param {React.ChangeEvent<HTMLInputElement>} event - formのsubmitイベント
   * @returns {void}
   */
  const handleEmailCodeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.currentTarget.value;
    setEmailCode(() => value);
  };

  /**
   * コード送信ボタン押下時のイベントハンドラ。
   * @param {React.SyntheticEvent<HTMLFormElement>} event - formのsubmitイベント
   * @returns {Promise<void>}
   */
  const handleEmailCodeSubmit = (event: React.SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    dispatch(completeEmailCode(emailCode));
  };

  /**
   * メールコード再送時のイベントハンドラ。
   * @returns {void}
   */
  const handleResendEmailCode = (): void => {
    const params: SignInInput = {
      username: email,
      password,
    };
    dispatch(cognitoLogin(params));
  };

  // VIEW
  // ================================================================================

  return (
    <>
      {newPasswordRequired ? (
        <NewPasswordForm password1={password1} password2={password2} isValid={isValidNewPassword} isLoading={isLoading} handleChange={handleNewPasswordChange} handleSubmit={handleNewPasswordSubmit} />
      ) : emailCodeRequired ? (
        <EmailCodeForm
          emailCode={emailCode}
          isValid={isValidEmailCode}
          isLoading={isLoading}
          handleChange={handleEmailCodeChange}
          handleSubmit={handleEmailCodeSubmit}
          handleResendEmailCode={handleResendEmailCode}
        />
      ) : (
        <IdPasswordForm email={email} password={password} isValid={isValidIdPassword} isLoading={isLoading} handleChange={handleIdPasswordChange} handleSubmit={handleIdPasswordSubmit} />
      )}
    </>
  );
};

export default LoginForm;
